import { useEffect, useState } from "react";
import { TrendingUp, Target } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";

export const RevenueTracker = () => {
  const { t, lang } = useLang();
  const rv = t.dash.revenue;
  const [data, setData] = useState(null);
  const [goalEdit, setGoalEdit] = useState("");

  useEffect(() => {
    api.get("/stats/revenue").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  const saveGoal = async () => {
    const amount = Math.max(0, Number(goalEdit) || 0);
    await api.put("/settings/revenue-goal", { amount });
    setData((d) => ({ ...d, goal: amount }));
    setGoalEdit("");
  };

  const progress = data.goal > 0 ? Math.min(100, Math.round((data.month_added / data.goal) * 100)) : 0;

  const monthLabel = (m) =>
    new Date(`${m}-01T00:00:00`).toLocaleDateString(lang === "fr" ? "fr-BE" : "en-GB", { month: "short", year: "2-digit" });
  const chart = data.months.map((m) => ({ ...m, name: monthLabel(m.month) }));

  return (
    <div className="bg-[#111111] border border-white/10 p-6 md:p-8 mb-10" data-testid="revenue-tracker">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40 mb-6">
        <TrendingUp className="w-4 h-4 text-emerald-400" /> {rv.title}
      </p>
      <div className="grid md:grid-cols-3 gap-8 items-center">
        <div>
          <p className="font-display text-4xl font-semibold text-emerald-400" data-testid="revenue-mrr">
            €{data.mrr.toLocaleString("fr-BE")}
            <span className="text-base font-normal text-white/50">{rv.perMonth}</span>
          </p>
          <p className="text-xs text-white/40 mt-1">≈ €{data.annual.toLocaleString("fr-BE")}{rv.perYear}</p>
          <p className="text-xs uppercase tracking-widest text-white/30 mt-2">{rv.mrr}</p>
          {data.mrr === 0 && <p className="text-xs text-white/40 mt-3 leading-relaxed">{rv.empty}</p>}
          <div className="mt-5 pt-4 border-t border-white/10" data-testid="revenue-goal-block">
            <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-white/30 mb-2">
              <Target className="w-3.5 h-3.5 text-[#FF7900]" /> {rv.goalTitle}
            </p>
            {data.goal > 0 ? (
              <>
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="text-white/60">€{data.month_added.toLocaleString("fr-BE")} / €{data.goal.toLocaleString("fr-BE")}{rv.perMonth}</span>
                  <span className="text-[#FF7900] font-semibold" data-testid="revenue-goal-pct">{progress}%</span>
                </div>
                <div className="h-1.5 bg-white/5" data-testid="revenue-goal-bar">
                  <div className="h-1.5 bg-[#FF7900] transition-[width] duration-500" style={{ width: `${progress}%` }} />
                </div>
              </>
            ) : (
              <p className="text-xs text-white/40 mb-2">{rv.noGoal}</p>
            )}
            <div className="flex gap-2 mt-3">
              <input
                type="number"
                min="0"
                value={goalEdit}
                onChange={(e) => setGoalEdit(e.target.value)}
                placeholder={data.goal > 0 ? String(data.goal) : rv.goalPlaceholder}
                data-testid="revenue-goal-input"
                className="w-28 bg-black/40 border border-white/15 px-2.5 py-1.5 text-xs outline-none focus:border-[#FF7900] transition-colors duration-300"
              />
              <button
                onClick={saveGoal}
                disabled={goalEdit === ""}
                data-testid="revenue-goal-save"
                className="text-xs border border-white/15 hover:border-[#FF7900] hover:text-[#FF7900] disabled:opacity-40 px-3 py-1.5 rounded-full transition-colors duration-300"
              >
                {rv.goalSave}
              </button>
            </div>
          </div>
        </div>
        <div className="md:col-span-2 h-40">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chart} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="mrrFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 11 }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => `€${v}`} />
              <Tooltip
                contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.15)", fontSize: 12 }}
                labelStyle={{ color: "rgba(255,255,255,0.6)" }}
                formatter={(v) => [`€${v}${rv.perMonth}`, rv.tooltip]}
              />
              <Area type="monotone" dataKey="mrr" stroke="#10b981" strokeWidth={2} fill="url(#mrrFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
